// 幣別顯示設定的唯一來源。
//
// 符號與位置一律來自 /get-config 的 supportCurrency（以站台幣別代碼為 key）：
//   supportCurrency: { VNDK: { symbol: 'K₫', position: 'back' } }
//
// runtimeConfig 的 currencyUnit / currencySymbol 已不再是顯示來源，只留在
// resolveCurrencyMeta 內部當後端未上線時的退路（見下方 fallback 階梯）。
// 呼叫端一律走 useFormat() 的 formatAmount。
//
// 本檔與 client-pwa-cash/utils/currency.js 為同一份契約，修改請同步兩邊。

// 千單位站台（金額以 1000 為一單位顯示，如越南 K₫）。
// 對應 client-pwa-cash/utils/currencyNormalize.js 的 CURRENCY_NORMALIZE（multiply > 1）。
export const THOUSAND_UNIT_CURRENCIES = new Set(['VNDK', 'IDRK'])

export const AMOUNT_KIND = {
  FIAT: 'FIAT',
  CRYPTO: 'CRYPTO',
  POINTS: 'POINTS',
}

// Unicode 雙向隔離：把金額 token 包起來，避免 RTL 語系句子裡數字與符號被 UBA 重排。
const LRI = '⁦'
const PDI = '⁩'

const EMPTY_META = { code: '', symbol: '', position: 'back', space: false, source: 'none' }

/**
 * 判斷是否為千單位站台。
 * @param {string} code 幣別代碼
 * @returns {boolean}
 */
export const isThousandUnitCurrency = (code) => THOUSAND_UNIT_CURRENCIES.has(code)

/**
 * 解析站台幣別的顯示設定。永不 throw、永不回 undefined。
 *
 * fallback 階梯：
 *   1. supportCurrency[currency] 有效 → 照 API（position 非 'front' 一律當 'back'）
 *   2. 後端未提供 → 用 env 組出與現行 production 逐字相同的字串（K₫），確保可安全 rollback
 *   3. env 也空 → 退成幣別代碼（VNDK）
 *   4. get-config 尚未回來 → 只顯示數字，不留多餘空格
 *
 * @param {Object} [siteConfig] get-config 解碼後的物件
 * @param {{ unit?: string, symbol?: string }} [envFallback] runtimeConfig 的 currencyUnit / currencySymbol
 * @returns {{ code: string, symbol: string, position: 'front'|'back', space: boolean, source: 'api'|'env'|'code'|'none' }}
 */
export const resolveCurrencyMeta = (siteConfig, envFallback = {}) => {
  const code = siteConfig?.currency || ''
  const entry = code ? siteConfig?.supportCurrency?.[code] : null

  if (entry && typeof entry.symbol === 'string' && entry.symbol !== '') {
    const position = entry.position === 'front' ? 'front' : 'back'
    return {
      code,
      symbol: entry.symbol,
      position,
      space: typeof entry.space === 'boolean' ? entry.space : position === 'back',
      source: 'api',
    }
  }

  const envSymbol = `${envFallback.unit || ''}${envFallback.symbol || ''}`
  if (envSymbol) {
    return { code, symbol: envSymbol, position: 'back', space: true, source: 'env' }
  }

  if (code) {
    return { code, symbol: code, position: 'back', space: true, source: 'code' }
  }

  return { ...EMPTY_META }
}

/**
 * 把已格式化的數字字串與幣別符號組成完整 token。
 *
 * @param {string} formatted 已經過 getFormatted 的字串
 * @param {{ symbol?: string, position?: string, space?: boolean }} [meta] 幣別顯示設定
 * @param {boolean} [isolate] 是否以 LRI/PDI 包住整個 token（要塞進 i18n 句子時必須）
 * @returns {string}
 */
export const composeCurrency = (formatted, meta, isolate = false) => {
  const symbol = meta?.symbol || ''
  if (!symbol) return formatted

  const gap = meta.space ? ' ' : ''
  const token = meta.position === 'front' ? `${symbol}${gap}${formatted}` : `${formatted}${gap}${symbol}`

  return isolate ? `${LRI}${token}${PDI}` : token
}

/**
 * 判定一筆金額該用什麼單位顯示，收斂原本散在各畫面的 crypto / POINTS 判斷。
 * 涵蓋交易紀錄 item、提款綁定銀行、活動獎勵三種資料形狀。
 *
 * @param {Object} [item]
 * @param {boolean} [item.isCrypto] 交易紀錄的加密貨幣旗標
 * @param {string} [item.bindingType] 綁定類型：'BANK' | 'EWALLET' | 'CRYPTO'
 * @param {string} [item.cryptoCurrency] 加密幣種，如 USDT
 * @param {string} [item.currency] 該筆資料的幣別欄，可能是 'POINTS' 或加密幣代碼
 * @param {string} [item.rewardType] 'POINTS' | 'CASH' | 'GAME' | 'PRIZE' | 'FREE'
 * @returns {{ kind: string, unit: string }}
 */
export const resolveAmountKind = (item = {}) => {
  const { isCrypto, bindingType, cryptoCurrency, currency, rewardType } = item

  // POINTS 先判：點數紀錄可能同時帶 currency 欄，且點數永遠不是加密貨幣
  if (rewardType === 'POINTS' || currency === 'POINTS') {
    return { kind: AMOUNT_KIND.POINTS, unit: '' }
  }

  if (isCrypto === true || bindingType === 'CRYPTO') {
    return { kind: AMOUNT_KIND.CRYPTO, unit: cryptoCurrency || currency || '' }
  }

  return { kind: AMOUNT_KIND.FIAT, unit: '' }
}
